import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  PrizedropPrizeDetails,
  PrizeDropWinSocketResponse,
  Promotion,
  PromotionType,
  TournamentUserPrizeDetails,
  WinNotificationType,
} from '../../../api/fetchers/promotionsV2/types/promotions';
import { fetchAssignedTablesAction } from '../../../store/apiActions/fetchAssignedTablesAction';
import {
  fetchCashbackScoreAction,
  fetchPromotionsAction,
  fetchPromotionsJwtAction,
  fetchLeaderboardAction,
  fetchLeaderboardAroundMeAction,
  fetchPromotionsPointsAction,
  fetchPrizesAndWinnersAction,
  postPromoConsentAction,
  fetchRoundHistoryAction,
  fetchRoundHistoryByWSAction,
} from '../../apiActions/fetchPromotionsActionV2';

import type { PromotionPointsJSON } from './../../../api/fetchers/promotionsV2/types/promotionsApi';
import type { PromoPoint, PromosHydrationData, PromotionsV2State } from './types';
import { PromosPopup } from './types';
import { isPromoOpted, displayPromoForConsent, getTopTournament, updatePromosPopup, isPromoActiveNow } from './utils';

export const promotionsV2Slice = createSlice({
  name: 'promotionsV2',
  initialState: {
    promotions: [],
    points: [],
    promosPopup: null,
    showPromoEar: false,
    selectedPromoId: null,
    isOptPromosLoading: false,
    assignedTables: null,
    isNewPromoAvailable: false,
    winNotifications: [],
    roundHistory: [],
    roundHistoryByWs: [],
    isRoundHistoryLoading: false,
    showDetailsModal: false,
    showScorePopup: false,
    isPromotionsInitialised: false,
  } as PromotionsV2State,
  reducers: {
    hydrateSlice: (state, action: PayloadAction<PromosHydrationData>) => {
      state.userId = action.payload.userId ?? state.userId;
      state.gameId = action.payload.gameId ?? state.gameId;
      state.lang = action.payload.lang ?? state.lang;
      state.currency = action.payload.currency ?? state.currency;
      state.screenName = action.payload.screenName ?? state.screenName;
      state.casinoRegion = action.payload.casinoRegion ?? state.casinoRegion;
      state.envGs = action.payload.envGs ?? state.envGs;
      state.envPromo = action.payload.envPromo ?? state.envPromo;
      state.jwtToken = action.payload.jwtToken ?? state.jwtToken;
      state.jsessionId = action.payload.jsessionId ?? state.jsessionId;
      state.isBetOpenStatus = action.payload.isBetOpenStatus ?? state.isBetOpenStatus;
      state.totalBet = action.payload.totalBet ?? state.totalBet;
      state.prizeDropWinMessage = action.payload.prizeDropWinMessage ?? state.prizeDropWinMessage;
      state.isResponsive = action.payload.isResponsive ?? state.isResponsive;
      state.featureConfig = action.payload.featureConfig ?? state.featureConfig;
    },
    pushPopup: (state, action: PayloadAction<PromosPopup>) => {
      state.promosPopup = action.payload;
    },
    dismissPopup: (state) => {
      state.promosPopup = null;
    },
    setShowDetailsModal: (state) => {
      state.showDetailsModal = !state.showDetailsModal;
    },
    setShowScorePopup: (state) => {
      //clear round history
      if (!state.showScorePopup) {
        state.roundHistory = [];
        state.roundHistoryByWs = [];
      }
      state.showScorePopup = !state.showScorePopup;
    },
    setSelectedPromoId: (state, action: PayloadAction<number>) => {
      state.selectedPromoId = action.payload;
    },
    setPrizeDropSocketResponse: (state, action: PayloadAction<PrizeDropWinSocketResponse>) => {
      const message = action.payload;
      const promo = state.promotions.find((promo) => promo.id == Number(message.pdid));
      if (promo) {
        const winDetails: WinNotificationType = {
          promoType: promo.type,
          promoId: promo.id,
          name: promo.name,
          brandName: promo.brandName,
          isAsia: promo.isAsian,
          winType: message.prizeType,
          prize: message.prizeType === 'G' ? message.textPrize : message.amount,
          endDate: promo.activeDuration.toEpoch,
          ruleDetails: promo.ruleDetails,
          autoPay: message.autoPay,
          minBetLimit: promo.prizeDetails.minimumBetLimit,
          extraData: {
            bet: Number(message.bet),
            multiplier: Number(message.mul),
            prizes: (promo.prizeDetails as PrizedropPrizeDetails).prizes,
          },
        };
        state.winNotifications = [winDetails];
        state.promosPopup = PromosPopup.PromosWinNotification;
      }
    },
    setWinNotifications: (state, action: PayloadAction<WinNotificationType[]>) => {
      state.winNotifications = action.payload;
      state.promosPopup = state.winNotifications.length > 0 ? PromosPopup.PromosWinNotification : null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromotionsAction.fulfilled, (state, { payload }) => {
        const promotions = payload.promotions;
        const haveOptedPromos = promotions.some((p) => isPromoOpted(p));
        const showPromosConsentModal = promotions.some((p) => displayPromoForConsent(p));
        state.isPromotionsInitialised = true;
        state.promotions = promotions;
        state.winNotifications = payload.winNotifications;
        state.showPromoEar = haveOptedPromos;
        state.promosPopup = updatePromosPopup(showPromosConsentModal, state.winNotifications.length > 0);
      })
      .addCase(fetchPromotionsAction.rejected, (state) => {
        state.isPromotionsInitialised = true;
      })
      .addCase(fetchPromotionsJwtAction.fulfilled, (state, { payload }) => {
        state.jwtToken = payload;
      })
      .addCase(postPromoConsentAction.fulfilled, (state, { payload }) => {
        payload.promotions.forEach((promo: Promotion) => {
          const index = state.promotions.findIndex((_promo) => promo.id === _promo.id);
          if (index > -1) {
            state.promotions[index] = promo;
          }
        });
        state.isOptPromosLoading = false;

        const haveOptedPromos = state.promotions.some((p) => isPromoOpted(p));
        const showPromosConsentModal = state.promotions.some((p) => displayPromoForConsent(p));

        state.showPromoEar = haveOptedPromos;
        state.promosPopup = updatePromosPopup(showPromosConsentModal, state.winNotifications.length > 0);
      })
      .addCase(postPromoConsentAction.pending, (state) => {
        state.isOptPromosLoading = true;
      })
      .addCase(fetchAssignedTablesAction.fulfilled, (state, action) => {
        state.assignedTables = action.payload;
      })
      .addCase(fetchLeaderboardAction.fulfilled, (state, { payload }) => {
        if (payload) {
          const index = state.promotions.findIndex((_promo) => payload.tournamentId === _promo.id);
          if (index > -1) {
            (state.promotions[index]?.userPrizeDetails as TournamentUserPrizeDetails).leaderboard = payload.leaderboard;
          }
        }
      })
      .addCase(fetchLeaderboardAroundMeAction.fulfilled, (state, { payload }) => {
        if (payload) {
          const index = state.promotions.findIndex((_promo) => payload.tournamentId === _promo.id);
          if (index > -1) {
            (state.promotions[index]?.userPrizeDetails as TournamentUserPrizeDetails).leaderboard_aroundMe =
              payload.leaderboardAroundMe;
          }
        }
      })
      .addCase(fetchPrizesAndWinnersAction.fulfilled, (state, { payload }) => {
        if (payload) {
          const index = state.promotions.findIndex((_promo) => payload.prizeDropId === _promo.id);
          if (index > -1) {
            const promo = state.promotions[index] as Promotion<PromotionType.Prizedrop>;
            if (promo) {
              promo.prizeDetails.prizes = payload.prizes;
              promo.userPrizeDetails.winners = payload.winners;
              promo.userPrizeDetails.screenNameHidden = payload.screenNameHidden;
            }
          }
        }
      })
      .addCase(fetchCashbackScoreAction.fulfilled, (state, { payload }) => {
        if (payload?.score) {
          const index = state.promotions.findIndex((_promo) => payload.cahbackId === _promo.id);
          if (index > -1) {
            const promo = state.promotions[index] as Promotion<PromotionType.Cashback>;
            if (promo) {
              promo.userPrizeDetails.cashbackPercentage = payload.score.cashbackPercentage;
              promo.userPrizeDetails.cashbackScore = payload.score.cashbackScore;
              promo.userPrizeDetails.roundLimitExceeded = payload.score.roundLimitExceeded;
              promo.userPrizeDetails.totalScore = payload.score.totalScore;
            }
          }
        }
      })
      .addCase(fetchPromotionsPointsAction.fulfilled, (state, { payload }) => {
        const activePoints: Array<PromoPoint> = [];
        const { data } = payload;
        for (const promoId in data) {
          const _promoId = Number(promoId);
          const promo = state.promotions.find((promo) => promo.id === _promoId);
          if (promo) {
            if (isPromoActiveNow(promo)) {
              activePoints.push({
                ...(data[promoId] as PromotionPointsJSON),
                id: _promoId,
                toEpoch: promo.activeDuration.toEpoch,
                minimumBetLimit: promo.prizeDetails.minimumBetLimit,
              });
            }
          }
        }
        const topTournament = getTopTournament(activePoints);

        state.isNewPromoAvailable = payload.isNewPromoAvailable;
        state.points = activePoints;
        state.topTournament = topTournament;
      })
      .addCase(fetchRoundHistoryAction.fulfilled, (state, { payload }) => {
        state.isRoundHistoryLoading = false;
        state.roundHistory.push(...payload);
      })
      .addCase(fetchRoundHistoryByWSAction.fulfilled, (state, { payload }) => {
        state.isRoundHistoryLoading = false;
        state.roundHistoryByWs.push(...payload);
      })
      .addCase(fetchRoundHistoryAction.pending, (state) => {
        state.isRoundHistoryLoading = true;
      })
      .addCase(fetchRoundHistoryByWSAction.pending, (state) => {
        state.isRoundHistoryLoading = true;
      });
  },
});

export const promotionsV2Actions = promotionsV2Slice.actions;
